'use client';

import { use, useState, useMemo } from 'react';
import {
    Users,
    Search,
    Filter,
    Plus,
    MoreHorizontal,
    Mail,
    Phone,
    BadgeCheck,
    GraduationCap,
    ArrowLeft,
    Download,
    FileText,
    Briefcase,
    Building2,
    Calendar,
    ChevronRight,
    Edit2,
    Eye,
    BookOpen,
    ShieldCheck
} from 'lucide-react';
import Link from 'next/link';
import { notFound } from 'next/navigation';
import {
    getSchoolData,
    getSchoolName,
    SchoolUnit,
    Staff
} from '@/lib/data';

export default function GuruDataPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit } = use(params);
    const validUnits: SchoolUnit[] = ['paud', 'paud-pekanbaru', 'paud-kampar', 'paud-siak', 'sd', 'sd-pekanbaru', 'sd-kampar', 'smp', 'smk'];

    if (!validUnits.includes(unit as SchoolUnit)) {
        notFound();
    }

    const schoolUnit = unit as SchoolUnit;
    const schoolData = getSchoolData(schoolUnit);
    const schoolName = getSchoolName(schoolUnit);

    // Dynamic data fetching - filter for teachers
    const allStaff = ((schoolData as any)[`${unit.split('-')[0]}Staff`] || []) as Staff[];
    const guruList = allStaff.filter(s => s.department === 'Pengajaran' || s.position.toLowerCase().includes('guru'));

    // State
    const [searchTerm, setSearchTerm] = useState('');
    const [filterPosition, setFilterPosition] = useState('Semua Jabatan');
    const [filterSchool, setFilterSchool] = useState(unit.includes('-') ? unit : 'Semua Sekolah');

    // Get school list for the unit
    const schools = useMemo(() => {
        const baseUnit = unit.split('-')[0];
        const schoolList = (schoolData as any)[`${baseUnit}Schools`] || [];
        return ['Semua Sekolah', ...schoolList.map((s: any) => s.id)];
    }, [schoolData, unit]);

    const getSchoolNameById = (id: string) => {
        if (id === 'Semua Sekolah') return 'Semua Sekolah';
        const baseUnit = unit.split('-')[0];
        const schoolList = (schoolData as any)[`${baseUnit}Schools`] || [];
        const school = schoolList.find((s: any) => s.id === id);
        return school ? school.name : id;
    };

    const positions = useMemo(() => {
        const roles = Array.from(new Set(guruList.map((s: Staff) => s.position)));
        return ['Semua Jabatan', ...roles.sort()];
    }, [guruList]);

    const filteredGuru = useMemo(() => {
        return guruList.filter((s: Staff) => {
            const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                s.id.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesPosition = filterPosition === 'Semua Jabatan' || s.position === filterPosition;
            const matchesSchool = filterSchool === 'Semua Sekolah' || s.schoolId === filterSchool;
            return matchesSearch && matchesPosition && matchesSchool;
        });
    }, [guruList, searchTerm, filterPosition, filterSchool]);

    const stats = useMemo(() => {
        const currentList = guruList.filter((s: Staff) => filterSchool === 'Semua Sekolah' || s.schoolId === filterSchool);
        return {
            total: currentList.length,
            tetap: currentList.filter((s: Staff) => s.status === 'active').length, // Mock logic
            aktif: currentList.filter((s: Staff) => s.status === 'active').length,
        };
    }, [guruList, filterSchool]);

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-24 animate-in fade-in duration-700">
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="relative z-10 space-y-10">
                    <div className="flex flex-col gap-8">
                        <Link
                            href={`/yayasan/${unit}`}
                            className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest hover:text-primary transition-all group/back"
                        >
                            <ArrowLeft className="w-3.5 h-3.5 group-hover/back:-translate-x-1 transition-transform" />
                            Kembali ke Dashboard Unit
                        </Link>

                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                    <Users className="w-8 h-8" />
                                </div>
                                <div className="space-y-1">
                                    <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">Data Guru</h1>
                                    <div className="flex items-center gap-2">
                                        <p className="text-secondary font-medium">{schoolName}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <button className="px-6 py-3.5 bg-surface border border-border-main rounded-2xl text-[10px] font-bold text-foreground hover:bg-white hover:border-primary transition-all shadow-sm flex items-center gap-2 uppercase tracking-widest">
                                    <Download className="w-4 h-4 text-muted" /> Export
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                    { label: 'Total Guru', value: stats.total, icon: Users, color: 'text-primary', bg: 'bg-primary-soft' },
                    { label: 'Status Aktif', value: stats.aktif, icon: BadgeCheck, color: 'text-success', bg: 'bg-success-soft' },
                ].map((item, idx) => (
                    <div key={idx} className="bg-surface p-6 rounded-4xl border border-border-main shadow-sm group hover:shadow-md transition-all">
                        <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 ${item.bg} ${item.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                <item.icon className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest">{item.label}</p>
                                <p className="text-2xl font-bold text-foreground">{item.value}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </section>

            <section className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden min-h-[500px] flex flex-col">
                <div className="p-6 border-b border-border-main flex flex-col md:flex-row md:items-center justify-between gap-4 bg-background/30">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                        <input
                            type="text"
                            placeholder="Cari nama atau NIPN..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-2xl text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-background border border-border-main px-4 py-2 rounded-2xl">
                            <Building2 className="w-4 h-4 text-muted" />
                            <select
                                className="bg-transparent border-none text-[10px] font-bold uppercase tracking-widest text-secondary outline-none cursor-pointer"
                                value={filterSchool}
                                onChange={(e) => setFilterSchool(e.target.value)}
                            >
                                {schools.map(sId => (
                                    <option key={sId} value={sId}>{getSchoolNameById(sId)}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-2 bg-background border border-border-main px-4 py-2 rounded-2xl">
                            <Briefcase className="w-4 h-4 text-muted" />
                            <select
                                className="bg-transparent border-none text-[10px] font-bold uppercase tracking-widest text-secondary outline-none cursor-pointer"
                                value={filterPosition}
                                onChange={(e) => setFilterPosition(e.target.value)}
                            >
                                {positions.map(pos => (
                                    <option key={pos} value={pos}>{pos}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto grow">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">NIPN & IDENTITAS</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Jabatan</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Status</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main">
                            {filteredGuru.length > 0 ? filteredGuru.map((staff: Staff) => (
                                <tr key={staff.id} className="hover:bg-primary-soft/10 transition-colors group">
                                    <td className="px-8 py-5">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-linear-to-br from-primary/10 to-primary/20 flex items-center justify-center text-primary font-bold text-xs uppercase shadow-inner">
                                                {staff.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2)}
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">{staff.name}</p>
                                                <p className="text-[10px] text-muted font-bold tabular-nums">NIPN: {staff.nipn || staff.id}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5 whitespace-nowrap">
                                        <p className="text-xs font-bold text-foreground">{staff.position}</p>
                                    </td>
                                    <td className="px-8 py-5 whitespace-nowrap">
                                        <span className={`px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider border ${staff.status === 'active'
                                            ? 'bg-success-soft text-success border-success/10'
                                            : 'bg-muted-soft text-muted border-muted/10'
                                            }`}>
                                            {staff.status}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5 text-right whitespace-nowrap">
                                        <div className="flex items-center justify-end">
                                            <Link
                                                href={`/yayasan/${unit}/staff/${staff.id}`}
                                                className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-muted hover:text-primary hover:border-primary transition-all shadow-sm"
                                            >
                                                <Eye className="w-5 h-5" />
                                            </Link>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={4} className="px-8 py-20 text-center">
                                        <p className="text-sm text-secondary font-medium">Tidak ada guru ditemukan</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="p-6 border-t border-border-main flex items-center justify-between bg-background/10">
                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">
                        Menampilkan {filteredGuru.length} dari {stats.total} Guru
                    </p>
                </div>
            </section>
        </main>
    );
}
